import ReactPlayer from "react-player"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import { FileDigitale, Sequenza } from "../types"
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { formatSeconds } from "../utils/video"
import { Box, IconButton, Stack, Toolbar, Slider, Typography, Tooltip } from "@mui/material"
import {
  PlayArrow,
  Pause,
  Replay5,
  Forward5,
  Replay10,
  Forward10,
  SkipNext,
  SkipPrevious,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material"

function SequenzePreview({
  sequenze = [],
  duration,
  currentSequence,
}: {
  sequenze?: Sequenza[]
  duration?: number
  currentSequence?: Sequenza | null
}) {
  if (!duration) {
    return null
  }

  return (
    <Box sx={{ width: "100%", height: 16, position: "relative", backgroundColor: "grey.400" }}>
      {sequenze?.map((sequenza, index) => {
        if (!duration) {
          return null
        }
        const startPercent = sequenza.tcin_seconds
        const endPercent = sequenza.tcout_seconds
        const width = endPercent - startPercent

        const isCurrent = currentSequence?.id === sequenza.id

        return (
          <Tooltip
            title={`Sequenza ${index + 1}`}
            placement="top"
            key={index}
            PopperProps={{
              container: document.fullscreenElement || undefined,
            }}
          >
            <Box
              aria-hidden={true}
              sx={{
                position: "absolute",
                left: `${(startPercent / duration) * 100}%`,
                width: `${(width / duration) * 100}%`,
                height: "100%",
                opacity: 0.75,
                backgroundColor: isCurrent ? "primary.main" : "secondary.light",
                "&:hover": {
                  backgroundColor: "secondary.main",
                },
              }}
            />
          </Tooltip>
        )
      })}
    </Box>
  )
}

function Duration({ className, seconds }: { className?: string; seconds: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <time dateTime={`P${Math.round(seconds)}S`} className={className}>
        <Typography variant="h5">{formatSeconds(seconds)}</Typography>
      </time>
    </div>
  )
}

type SequenzePlayerProps = {
  sequenze?: Sequenza[]
  fileDigitale?: FileDigitale
  ref?: React.Ref<any>
  fullScreen?: boolean
  setCurrentSequence: (sequenza: Sequenza | null) => void
  filterSequence?: string
}

export default function InternalPlayer({
  fileDigitale,
  ref,
  sequenze,
  fullScreen,
  setCurrentSequence,
  filterSequence,
}: SequenzePlayerProps) {
  const playerRef = useRef<ReactPlayer>(null)

  const [duration, setDuration] = useState(0)
  const [played, setPlayed] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)

  const seeking = useRef(false)

  useImperativeHandle(
    ref,
    () => {
      return {
        getCurrentTime() {
          if (!played) {
            return
          }
          return played * duration
        },
        setPlayed(value: number) {
          if (isNaN(value)) {
            return
          }
          setPlayed(value)
        },
      }
    },
    [played, duration, setPlayed]
  )

  const handleOnReady = () => {
    const player = playerRef.current
    if (!player) return
    const duration = player.getDuration()
    setDuration(duration)
    if (filterSequence) {
      const sequence = sequenze?.find((s) => s.id === Number(filterSequence))
      if (sequence) {
        console.log("Found sequence:", sequence, playerRef.current)
        setPlayed(sequence.tcin_seconds / duration)
      }
    }
  }

  const handleDurationChange = () => {
    const player = playerRef.current
    if (!player) return
    const duration = player.getDuration()
    setDuration(duration)
  }

  const handleTimeUpdate = useCallback(() => {
    const player = playerRef.current
    // We only want to update time slider if we are not currently seeking
    if (!player || seeking.current) return

    const currentTime = player.getCurrentTime() || 0
    const duration = player.getDuration()
    setPlayed(currentTime / duration)
  }, [])

  const handleSeekChange = (_event: Event, value: any, _activeThumb: number) => {
    seeking.current = true
    if (isNaN(value)) {
      return
    }
    setPlayed(value)
  }

  const handleSeekCommitted = (_event: React.SyntheticEvent | Event, value: any) => {
    if (isNaN(value)) {
      return
    }
    seeking.current = false
    if (playerRef.current) {
      const currentTime = value * playerRef.current.getDuration()
      playerRef.current.seekTo(currentTime)
    }
  }

  const handleSeekDelta = useCallback((delta: number) => {
    const player = playerRef.current
    if (!player) return
    const currentTime = player.getCurrentTime() || 0
    const duration = player.getDuration()
    const newTime = Math.max(0, Math.min(currentTime + delta, duration))
    setPlayed(newTime / duration)
    player.seekTo(newTime)
  }, [])

  const currentSequence = useMemo(() => {
    if (!fileDigitale) {
      return null
    }
    const currentTime = played * duration
    const currentSequenze = sequenze?.filter((sequenza) => {
      return currentTime >= sequenza.tcin_seconds && currentTime <= sequenza.tcout_seconds
    })
    return currentSequenze?.[0]
  }, [played, duration, sequenze, fileDigitale])

  useEffect(() => {
    setCurrentSequence(currentSequence || null)
  }, [currentSequence])

  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up("xl"))

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack flexGrow={1} sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
        <ReactPlayer
          ref={playerRef}
          width={fullScreen ? "90%" : matches ? "80%" : "100%"}
          height={fullScreen ? "auto" : "100%"}
          url={fileDigitale?.file}
          volume={volume}
          controls={false}
          playing={playing}
          muted={muted}
          onReady={handleOnReady}
          onDuration={handleDurationChange}
          onTimeUpdate={handleTimeUpdate}
        />
        <SequenzePreview sequenze={sequenze} duration={duration} currentSequence={currentSequence} />
      </Stack>

      <Slider
        aria-label="Progress"
        value={played || 0}
        onChange={handleSeekChange}
        onChangeCommitted={handleSeekCommitted}
        min={0}
        max={0.999999999}
        step={0.0001}
      />

      <Stack direction="row" justifyContent="center">
        <Toolbar variant="dense">
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => handleSeekDelta(-10)}>
            <Replay10 />
          </IconButton>
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => handleSeekDelta(-5)}>
            <Replay5 />
          </IconButton>
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => handleSeekDelta(-1)}>
            <SkipPrevious />
          </IconButton>
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => setPlaying((prev) => !prev)}>
            {playing ? <Pause /> : <PlayArrow />}
          </IconButton>
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => handleSeekDelta(1)}>
            <SkipNext />
          </IconButton>
          <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }} onClick={() => handleSeekDelta(5)}>
            <Forward5 />
          </IconButton>
          <IconButton color="inherit" aria-label="menu" onClick={() => handleSeekDelta(10)}>
            <Forward10 />
          </IconButton>
        </Toolbar>
      </Stack>
      <Stack justifyContent="space-between" direction={"row"}>
        <Stack style={{ width: "30%" }} direction={"row"} alignItems="center">
          <IconButton
            color="inherit"
            aria-label="menu"
            sx={{ mr: 1 }}
            onClick={() => {
              // setVolume((prev) => (prev === 0 ? 1 : 0))
              setMuted((prev) => !prev)
            }}
          >
            {muted ? <VolumeOff /> : <VolumeUp />}
          </IconButton>
          <Slider
            size="small"
            aria-label="Volume"
            disabled={muted}
            value={volume}
            onChange={(_e, value) => {
              setVolume(value as number)
            }}
            min={0}
            max={1}
            step={0.01}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              if (value === 0) {
                return "Mute"
              }
              return `${Math.round(value * 100)}%`
            }}
            sx={{ width: 100 }}
          />
        </Stack>
        <Duration seconds={duration ? duration * played : 0} />
        <div style={{ width: "30%" }}></div>
      </Stack>
    </Stack>
  )
}
